import { createSelector } from '@reduxjs/toolkit';
import { isEqual, omit } from 'lodash';

import {
  CALL_RESET_BOUNDARY_CONFIG,
  CALL_RESET_SLICE_KEYS,
  type CallResetSliceKey,
} from './callResetBoundaryConfig';

import type { RootState } from 'features/app/store/rootReducer';
import { rootReducer } from 'features/app/store/rootReducer';
import { CallPageStatus } from 'features/call/call-ui-state/enums';
import { selectCallPageStatus } from 'features/call/call-ui-state/store';

let callResetBaseline: Pick<RootState, CallResetSliceKey> | null = null;

export const getCallResetBaselineState = (): Pick<
  RootState,
  CallResetSliceKey
> => {
  if (!callResetBaseline) {
    const initialState = rootReducer(undefined, { type: '@@INIT' });

    callResetBaseline = Object.fromEntries(
      CALL_RESET_SLICE_KEYS.map((sliceKey) => [
        sliceKey,
        initialState[sliceKey],
      ])
    ) as Pick<RootState, CallResetSliceKey>;
  }

  return callResetBaseline;
};

const getCallResetBaseline = getCallResetBaselineState;

const compareSliceForLingeringData = (
  sliceKey: CallResetSliceKey,
  current: RootState[CallResetSliceKey],
  baseline: RootState[CallResetSliceKey]
) => {
  if (current === baseline) {
    return false;
  }

  const ignoredKeys = CALL_RESET_BOUNDARY_CONFIG[sliceKey].ignoredKeys;

  if (ignoredKeys?.length) {
    return !isEqual(omit(current, ignoredKeys), omit(baseline, ignoredKeys));
  }

  return !isEqual(current, baseline);
};

const isSliceDirty = (state: RootState, sliceKey: CallResetSliceKey) => {
  const current = state[sliceKey];

  if (current === undefined) {
    return false;
  }

  return compareSliceForLingeringData(
    sliceKey,
    current,
    getCallResetBaseline()[sliceKey]
  );
};

export const hasLingeringCallResetSliceData = (state: RootState): boolean =>
  CALL_RESET_SLICE_KEYS.some((sliceKey) => isSliceDirty(state, sliceKey));

const createAppSelector = createSelector.withTypes<RootState>();

export const selectHasLingeringCallData = createAppSelector(
  [(state: RootState) => state],
  (state) => hasLingeringCallResetSliceData(state)
);

export const selectHasLingeringCallState = createAppSelector(
  [selectHasLingeringCallData, selectCallPageStatus],
  (hasLingeringCallData, pageStatus) =>
    hasLingeringCallData || pageStatus !== CallPageStatus.IDLE
);

export const selectShouldEnforceCallStateResetBoundary =
  selectHasLingeringCallState;
